import React, { useState } from 'react';
import { useAuthStore } from '@/store/useAuthStore';

import Input from '@/components/ui/Input';
import Panel from '@/components/ui/Panel';
import { Mail, Lock, User as UserIcon, LogIn, Send } from 'lucide-react'; // Send for signup icon

const AuthPage: React.FC = () => {
  const [isLoginView, setIsLoginView] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState(''); // For signup
  const [formError, setFormError] = useState<string | null>(null);

  const { loginWithEmail, signupWithEmail, loginWithGoogle, authError, clearAuthError, isLoadingAuth } = useAuthStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearAuthError(); // Clear previous errors
    setFormError(null); // Clear form-specific errors

    try {
      if (isLoginView) {
        await loginWithEmail(email, password);
      } else {
        if (password.length < 6) {
          setFormError("Password must be at least 6 characters long.");
          return;
        }
        await signupWithEmail(email, password, displayName);
      }
      // Navigation will be handled by PrivateRoute/PublicRoute HOCs or auth listener redirecting
    } catch (error: any) {
      // error is already set in authStore, but we can set a form-specific one too
      const firebaseError = error?.code ? error.code.split('/')[1].replace(/-/g, ' ') : "An unexpected error occurred.";
      setFormError(`Failed: ${firebaseError}`);
      console.error("AuthPage error:", error);
    }
  };
  
  const handleGoogleSignIn = async () => {
    clearAuthError();
    setFormError(null);
    try {
      await loginWithGoogle();
    } catch (error: any) {
       const firebaseError = error?.code ? error.code.split('/')[1].replace(/-/g, ' ') : "An unexpected error occurred.";
      setFormError(`Google Sign-In Failed: ${firebaseError}`);
    }
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '2rem',
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: '2rem',
    background: 'linear-gradient(to right, #60a5fa, #a78bfa)',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
  };

  const formStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  };

  const dividerStyle: React.CSSProperties = {
    margin: '1.5rem 0',
    position: 'relative',
    textAlign: 'center',
  };

  const dividerLineStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
  };

  const dividerTextStyle: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    fontSize: '0.875rem',
    color: '#94a3b8',
    background: 'rgba(15, 23, 42, 0.9)',
    padding: '0 0.75rem',
  };

  const googleButtonStyle: React.CSSProperties = {
    background: 'rgba(30, 41, 59, 0.8)',
    color: '#dbeafe',
    padding: '0.75rem 1rem',
    borderRadius: '0.5rem',
    border: '1px solid rgba(96, 165, 250, 0.3)',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.75rem',
    width: '100%',
  };

  const switchTextStyle: React.CSSProperties = {
    marginTop: '2rem',
    textAlign: 'center',
    fontSize: '0.875rem',
    color: '#94a3b8',
  };

  const switchButtonStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    color: '#60a5fa',
    cursor: 'pointer',
    fontWeight: '600',
    textDecoration: 'underline',
    fontSize: '0.875rem',
  };

  return (
    <div className="flex flex-col items-center justify-center flex-grow text-center animate-fadeIn">
      <Panel 
        style={{ width: '100%', maxWidth: '28rem' }}
        padding="lg"
        border={true}
      >
        <h2 style={titleStyle}>
          {isLoginView ? 'Login to Your Account' : 'Create an Account'}
        </h2>
        
        <form onSubmit={handleSubmit} style={formStyle}>
          {!isLoginView && (
            <Input
              label="Display Name"
              id="displayName"
              type="text"
              leftIcon={UserIcon}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter your display name"
              required={!isLoginView}
            />
          )}
          <Input
            label="Email Address"
            id="email"
            type="email"
            leftIcon={Mail}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
          />
          <Input
            label="Password"
            id="password"
            type="password"
            leftIcon={Lock}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            required
          />

          {formError && (
            <p style={{ fontSize: '0.875rem', color: '#f87171', textAlign: 'center' }}>
              {formError}
            </p>
          )}
          {authError && !formError && (
            <p style={{ fontSize: '0.875rem', color: '#f87171', textAlign: 'center' }}>
              {(authError as any)?.code || (authError as Error)?.message}
            </p>
          )}
          
          <button
            type="submit"
            disabled={isLoadingAuth}
            style={{
              background: 'linear-gradient(to right, #2563eb, #7c3aed)',
              color: 'white',
              padding: '0.75rem 1rem',
              borderRadius: '0.5rem',
              border: 'none',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: isLoadingAuth ? 'not-allowed' : 'pointer',
              opacity: isLoadingAuth ? 0.5 : 1,
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              width: '100%',
            }}
            onMouseEnter={(e) => {
              if (!isLoadingAuth) {
                e.currentTarget.style.background = 'linear-gradient(to right, #1d4ed8, #6d28d9)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoadingAuth) {
                e.currentTarget.style.background = 'linear-gradient(to right, #2563eb, #7c3aed)';
              }
            }}
          >
            {isLoginView ? <LogIn size={20} /> : <Send size={20} />}
            {isLoginView ? 'Login' : 'Sign Up'}
          </button>
        </form>

        <div style={dividerStyle}>
          <div style={dividerLineStyle}>
            <div style={{ width: '100%', height: '1px', background: 'rgba(96, 165, 250, 0.2)' }} />
            </div>
          <div style={dividerTextStyle}>Or continue with</div>
          </div>

        <button
          onClick={handleGoogleSignIn}
          disabled={isLoadingAuth}
          style={googleButtonStyle}
          onMouseEnter={(e) => {
            if (!isLoadingAuth) {
              e.currentTarget.style.background = 'rgba(51, 65, 85, 0.8)';
              e.currentTarget.style.borderColor = 'rgba(96, 165, 250, 0.5)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isLoadingAuth) {
              e.currentTarget.style.background = 'rgba(30, 41, 59, 0.8)';
              e.currentTarget.style.borderColor = 'rgba(96, 165, 250, 0.3)';
            }
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 6.53l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Sign in with Google
        </button>

        <p style={switchTextStyle}>
          {isLoginView ? "Don't have an account?" : "Already have an account?"}{' '}
          <button
            onClick={() => { 
              setIsLoginView(!isLoginView); 
              clearAuthError(); 
              setFormError(null); 
              setEmail(''); 
              setPassword(''); 
              setDisplayName('');
            }}
            style={switchButtonStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#93c5fd';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#60a5fa';
            }}
          >
            {isLoginView ? 'Sign up' : 'Login'}
          </button>
        </p>
      </Panel>
    </div>
  );
};

export default AuthPage; 